'use client';

import React from 'react';
import { createPlatePlugin } from '@udecode/plate/react';
import { isHotkey } from 'is-hotkey';
import { Editor } from 'slate';
import { CustomAIMenu } from '@/components/plate-ui/custom-ai-menu';

// Custom AI Plugin Key
export const CUSTOM_AI_PLUGIN_KEY = 'customAI';

// AI Actions - Updated to match new API specification
export type AIAction =
  | 'rephrase'
  | 'improve'
  | 'shorten'
  | 'expand'
  | 'simplify'
  | 'formalize'
  | 'grammar_check'
  | 'summarize'
  | 'custom';

// AI Plugin State
export interface CustomAIPluginOptions {
  isOpen?: boolean;
  anchorElement?: HTMLElement | null;
  selectedText?: string;
  action?: AIAction | null;
  isLoading?: boolean;
}



// Create the custom AI plugin
export const CustomAIPlugin = createPlatePlugin<CustomAIPluginOptions>({
  key: CUSTOM_AI_PLUGIN_KEY,
  options: {
    isOpen: false,
    anchorElement: null,
    selectedText: '',
    action: null,
    isLoading: false,
  },

  render: {
    afterEditable: () => <CustomAIMenu />,
  },

  handlers: {
    onKeyDown: ({ editor, setOptions }) => (event) => {
      // Don't interfere with undo/redo shortcuts
      if (isHotkey('mod+z')(event) || isHotkey('mod+y')(event) || isHotkey('mod+shift+z')(event)) {
        return false; // Let the HistoryPlugin handle these
      }

      // Handle Cmd+J (Meta+J) hotkey
      if (isHotkey('meta+j')(event)) {
        event.preventDefault();

        // Get current selection or cursor position
        const selection = editor.selection;
        let anchorElement: HTMLElement | null = null;

        if (selection) {
          try {
            if (editor.api.isExpanded(selection)) {
              // Text is selected - get the last selected block
              const selectedBlocks = editor.api.blocks();
              if (selectedBlocks.length > 0) {
                const lastBlock = selectedBlocks.at(-1)![0];
                anchorElement = editor.api.toDOMNode(lastBlock);
              }
            } else {
              // Just cursor position - get current block
              const [currentBlock] = editor.api.block({ highest: true }) || [];
              if (currentBlock) {
                anchorElement = editor.api.toDOMNode(currentBlock);
              }
            }
          } catch (error) {
            console.warn('Could not get DOM node for selection:', error);
          }
        }

        // Fallback: get the editor container
        if (!anchorElement) {
          const editorElement = editor.api.toDOMNode(editor);
          if (editorElement) {
            const firstBlock = editorElement.querySelector('[data-slate-node="element"]');
            anchorElement = firstBlock as HTMLElement || editorElement;
          }
        }

        if (anchorElement) {
          // Get selected text
          let selectedText = '';
          if (selection && editor.api.isExpanded(selection)) {
            try {
              // Get the selected text using Slate's built-in method
              selectedText = Editor.string(editor, selection);
            } catch (error) {
              console.warn('Could not get selected text:', error);
              selectedText = '';
            }
          }

          setOptions({
            isOpen: true,
            anchorElement,
            selectedText,
            action: null,
            isLoading: false,
          });
        }

        return true; // Prevent default behavior
      }

      return false; // Let other handlers process the event
    },
  },
});


